/**
 * Sign in. SPEC-0003 §1.
 *
 * Google and email/password reach identical functionality. There is no
 * registration form, and that is permanent rather than pending: invite-only is
 * the primary path, and self-registration was considered and ruled out
 * (ADR-0012 addendum). Sign-in failures are deliberately indistinguishable —
 * the UI must never leak which addresses have accounts.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMark } from '../components/GoogleMark';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../lib/auth';

export function SignIn() {
	const { signInWithGoogle, signInWithPassword, resetPassword } = useAuth();
	const navigate = useNavigate();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function run(action: () => Promise<void>, then?: () => void) {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			await action();
			then?.();
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
		} finally {
			setBusy(false);
		}
	}

	function onSubmit(event: FormEvent) {
		event.preventDefault();
		void run(() => signInWithPassword(email, password), () => navigate('/'));
	}

	return (
		<main className="bg-base-100 flex min-h-screen items-center justify-center p-6">
			{/* Pinned to the viewport rather than sitting in the card's flow, where
			    it crowded the wordmark and read as part of the branding. */}
			<div className="fixed top-4 right-4 z-10">
				<ThemeToggle />
			</div>

			<div className="w-full max-w-sm">
				{/* Sized and centred so the sign-in page reads as Picklii's rather
				    than as a bare form. */}
				<div className="mb-6 flex flex-col items-center gap-1">
					<Logo className="gap-2.5 text-4xl" iconClassName="size-10" />
					<p className="text-base-content/70 text-sm">Sold hours vs PSA budget</p>
				</div>

				<div className="card bg-base-200 border-base-300 border shadow-xs">
					<div className="card-body gap-4">
						<div>
							<h2 className="card-title text-base">Sign in</h2>
							<p className="text-base-content/70 text-sm">
								Picklii is invite-only. If your account isn't enabled, ask your administrator.
							</p>
						</div>

						{error !== null && (
							<div className="alert alert-error alert-soft" role="alert">
								<span>{error}</span>
							</div>
						)}
						{notice !== null && (
							<div className="alert alert-info alert-soft" role="status">
								<span>{notice}</span>
							</div>
						)}

						{/*
						 * Solid surface plus an explicit stroke. The theme sets
						 * --border to 0.5px, so a default button's edge all but
						 * vanishes — which is why this read as a label rather than a
						 * control. In dark mode the fill alone only reaches 1.84:1
						 * against the card; the stroke brings the boundary to 3.9:1,
						 * clearing the 3:1 WCAG asks for non-text UI. Google's own
						 * dark-theme button is outlined for the same reason.
						 */}
						<button
							className="btn btn-neutral btn-block border-base-content/30 gap-3 border"
							disabled={busy}
							onClick={() => void run(signInWithGoogle, () => navigate('/'))}>
							<GoogleMark className="size-5" />
							Continue with Google
						</button>

						<div className="divider text-xs">or</div>

						<form className="flex flex-col gap-3" onSubmit={onSubmit}>
							<label className="floating-label">
								<span>Email</span>
								<input
									type="email"
									className="input input-bordered w-full"
									placeholder="Email"
									autoComplete="username"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</label>
							<label className="floating-label">
								<span>Password</span>
								<input
									type="password"
									className="input input-bordered w-full"
									placeholder="Password"
									autoComplete="current-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
							</label>
							<button className="btn btn-primary btn-block" type="submit" disabled={busy}>
								{busy ? <span className="loading loading-spinner loading-sm" /> : 'Sign in'}
							</button>
						</form>

						<button
							className="btn btn-ghost btn-sm"
							disabled={busy || email === ''}
							onClick={() =>
								void run(
									() => resetPassword(email),
									() =>
										// Always reports success, whether or not the address exists.
										setNotice('If that address has an account, a reset link is on its way.'),
								)
							}>
							Forgot your password?
						</button>
					</div>
				</div>
			</div>
		</main>
	);
}
